import React, { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, FormControl, FormHelperText } from "@mui/material";
import type { FileUploadConditions } from "@components/FileUpload/FileUpload.types";
import type { UploadEntry } from "@components/FileUpload/uploadStore";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import { t } from "i18next";
import debounce from "lodash.debounce";
import { Controller, useFormContext } from "react-hook-form";
import type { FieldValues, Path, PathValue, UseFormReturn } from "react-hook-form";
import FormItemFieldRenderer from "./FormItemFieldRenderer";
import type { DateConstraint, FormItemType, Option } from "./FormItemFieldRenderer";

interface Props<T extends FieldValues, K extends Path<T> = Path<T>> {
  validationLabel?: string;
  maxCharsCustomError?: string;
  maxDigits?: number;
  name: K;
  maxFiles?: number;
  dir?: string;
  label?: string;
  labelId?: string;
  testId: string;
  type?: FormItemType;
  fileVariant?: "button";
  options?: Option[];
  fullWidth?: boolean;
  onFieldChange?: (key: K, value: PathValue<T, K>) => void;
  onUpload?: (key: K, files: File[]) => void;
  onReject?: () => void;
  onRemoveFile?: (name: K, index: number) => void;
  slotProps?: { htmlInput?: React.InputHTMLAttributes<HTMLInputElement> };
  datePickerSlotProps?: any;
  datePickerDisablePortal?: boolean;
  timePicker?: boolean;
  uploadConditions?: FileUploadConditions;
  allowedFileExtensions?: string[];
  files?: UploadEntry[];
  setFiles?: (files: UploadEntry[]) => void;
  fileValidation?: (files: File[]) => Promise<string | null | void> | string | null | void;
  value?: string;
  defaultValue?: string | number | boolean | null; // default value for the field
  horizontalRadioButtons?: boolean; // true if radio buttons should be displayed horizontally
  required?: boolean;
  maxChars?: number;
  minRows?: number;
  uploadDescription?: string;
  disabled?: boolean;
  allowReplaceExisting?: boolean;
  uploadStoreKey?: string;
  autocompleteValue?: Option | null;
  dateConstraint?: DateConstraint;
  minDate?: Date;
  maxDate?: Date;
  decimals?: number;
  showCharCounter?: boolean;
  actionInLabel?: boolean;
  textForActionInLabel?: string;
  onActionClicked?: () => void;
  inlineRadioButtons?: boolean;
  radioValidationBelowButtons?: boolean;
  inlineRadioGroupBasis?: string;
  inlineRadioOptionBasis?: string;
  children?: React.ReactNode;
  warningMsg?: string | null;
  enforceUppercase?: boolean;
  combinedInput?: boolean;
  inputLabel?: string;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  formatOnChange?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  forceHighZIndexPopper?: boolean;
  notSuccess?: boolean;
  canBeginWithZero?: boolean;
  disableNativeNumberPattern?: boolean;
  validateDrop?: (selectedCount: number) => string | null;
  showErrorMsg?: boolean;
}

// get object value by path, e.g. getByPath(obj, "a.b.c")
export const getByPath = (obj: Record<string, unknown>, path: string) => {
  if (!obj || !path) return undefined;
  return path
    .split(".")
    .reduce<unknown>(
      (acc, key) =>
        acc && typeof acc === "object" ? (acc as Record<string, unknown>)[key] : undefined,
      obj,
    );
};

export const getErrorMessage = (
  errors: Record<string, unknown>,
  path: string,
): string | undefined => {
  const node = getByPath(errors, path) as { message?: unknown };
  return typeof node?.message === "string" ? node.message : undefined;
};

const FormItem = <T extends FieldValues, K extends Path<T> = Path<T>>({
  maxCharsCustomError,
  maxDigits,
  name,
  maxFiles,
  label,
  dir,
  labelId,
  testId,
  type = "text",
  fileVariant,
  options = [],
  fullWidth = true,
  onFieldChange,
  onUpload,
  onReject,
  onRemoveFile,
  slotProps,
  timePicker,
  uploadConditions,
  allowedFileExtensions,
  files,
  setFiles,
  fileValidation,
  horizontalRadioButtons,
  required = false,
  maxChars,
  defaultValue,
  minRows = 6,
  uploadDescription,
  disabled = false,
  allowReplaceExisting = false,
  uploadStoreKey,
  autocompleteValue,
  dateConstraint,
  minDate,
  maxDate,
  decimals = 0,
  showCharCounter = false,
  actionInLabel = false,
  textForActionInLabel = "",
  onActionClicked,
  inlineRadioButtons = false,
  radioValidationBelowButtons = false,
  inlineRadioGroupBasis = "240px",
  inlineRadioOptionBasis = "50%",
  children,
  warningMsg,
  enforceUppercase = false,
  combinedInput = false,
  inputLabel,
  onFocus,
  formatOnChange,
  datePickerSlotProps,
  forceHighZIndexPopper = false,
  validationLabel,
  notSuccess = false,
  canBeginWithZero = true,
  disableNativeNumberPattern = false,
  validateDrop,
  showErrorMsg = true,
}: Props<T, K>) => {
  const {
    control,
    formState: { errors },
    trigger,
    setError,
    clearErrors,
  } = useFormContext<T>();

  const labelForValidation = validationLabel ?? label;
  const error = getErrorMessage(errors as unknown as Record<string, unknown>, name);
  const [textLimitAttempted, setTextLimitAttempted] = useState(false);
  const shouldDebounceStoreSync = type === "text" || type === "textarea" || type === "number";
  const onFieldChangeRef = useRef(onFieldChange);

  useEffect(() => {
    onFieldChangeRef.current = onFieldChange;
  }, [onFieldChange]);

  const debouncedChange = useMemo(() => {
    if (!shouldDebounceStoreSync) return undefined;
    return debounce(
      (key: K, value: PathValue<T, K>) => onFieldChangeRef.current?.(key, value),
      600,
    );
  }, [shouldDebounceStoreSync]);
  useEffect(
    () => () => {
      const flush = (debouncedChange as { flush?: () => void } | undefined)?.flush;
      if (typeof flush === "function") flush();
    },
    [debouncedChange],
  );

  // const displayLabel = useMemo(() => {
  //   if (!label) return "";
  //   return required ? `${label} *` : label;
  // }, [label, required]);

  const isActionInLabel = actionInLabel && onActionClicked && textForActionInLabel;
  const shouldRenderRadioValidationBelowButtons =
    type === "radio" && inlineRadioButtons && radioValidationBelowButtons;
  const shouldUseGridLayout =
    showErrorMsg &&
    [
      "text",
      "number",
      "textarea",
      "autocomplete",
      "date-picker",
      "select",
      "multiple-select",
    ].includes(type);

  const warning = (
    <>
      <WarningAmberIcon />
      {warningMsg}
    </>
  );

  return (
    <FormControl
      fullWidth={fullWidth}
      error={!!error}
      sx={(theme) => ({
        "pb": 0,
        "display": shouldUseGridLayout ? "grid" : undefined,
        "gridTemplateRows": shouldUseGridLayout ? "1fr auto 1fr" : undefined,
        "alignContent": shouldUseGridLayout ? "center" : undefined,
        "height": shouldUseGridLayout ? "100%" : undefined,
        "& > *": shouldUseGridLayout ? { minWidth: 0 } : undefined, // prevent children from overflowing when using grid layout
        "& .MuiFormLabel-root": { color: theme.palette.text.primary },
        "& .MuiFormLabel-root.Mui-focused": { color: theme.palette.text.primary },
        "& .MuiFormLabel-root.Mui-error": {
          color: theme.palette.text.primary, // in the new design the label does not change color if there is an error
        },
        "&": inlineRadioButtons && {
          "display": "flex",
          "flexDirection": "row",
          "flexWrap": shouldRenderRadioValidationBelowButtons ? "wrap" : "nowrap",
          "alignItems": "center",
          "justifyContent": "space-between",
          "width": "100%",

          ".MuiFormLabel-root": {
            margin: 0,
            flex: 1,
          },
          ".MuiFormGroup-root": {
            "flexBasis": inlineRadioGroupBasis,
            "display": "flex",

            "justifyContent": "space-between",

            ".MuiFormControlLabel-root": {
              flexBasis: inlineRadioOptionBasis,
              marginRight: 0,
            },
          },
          ".form-item-helper-row": {
            width: "100%",
          },
        },
      })}
    >
      {label && !["file", "checkbox"].includes(type) && (
        <Box
          sx={shouldUseGridLayout ? { display: "flex", alignItems: "flex-end", mb: 1 } : { mb: 1 }}
        >
          <CustomFormLabel data-testid={labelId} disabled={disabled}>
            {label}
            {isActionInLabel && (
              <Box
                component="span"
                role="button"
                tabIndex={0}
                data-testid={labelId ? `${labelId}-action` : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  onActionClicked();
                }}
                sx={{
                  ml: 1,
                  p: 0,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "primary.main",
                }}
                className="underline"
              >
                {textForActionInLabel}
              </Box>
            )}
          </CustomFormLabel>
        </Box>
      )}

      <Controller<T>
        name={name}
        control={control as UseFormReturn<T>["control"]}
        render={({ field }) => {
          const valueLength = typeof field.value === "string" ? field.value.length : 0;
          const textOverflowAttempted =
            (type === "text" || type === "textarea") &&
            typeof maxChars === "number" &&
            textLimitAttempted;
          const derivedError =
            !error && textOverflowAttempted
              ? maxCharsCustomError ||
                t("form-validation.max-length", {
                  label: labelForValidation || label,
                  max: maxChars,
                })
              : undefined;
          const displayError = error || derivedError;
          const syncFieldChange = (value: PathValue<T, K>) => {
            if (shouldDebounceStoreSync) {
              debouncedChange?.(name, value);
              return;
            }
            onFieldChange?.(name, value);
          };

          const handleBlurTrim = (e?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const raw = e?.target?.value ?? field.value;
            const trimmed = typeof raw === "string" ? raw.trim() : raw;
            if (trimmed !== raw) {
              field.onChange(trimmed);
              syncFieldChange(trimmed as PathValue<T, K>);
            }
            if ((type === "text" || type === "textarea") && textLimitAttempted) {
              setTextLimitAttempted(false);
            }
            const flush = (debouncedChange as { flush?: () => void } | undefined)?.flush;
            if (typeof flush === "function") flush();
            field.onBlur();
          };

          const commitValue = (value: unknown) => {
            const nextValue = enforceUppercase ? String(value).toUpperCase() : value;
            if (
              (type === "text" || type === "textarea") &&
              typeof maxChars === "number" &&
              typeof nextValue === "string" &&
              nextValue.length <= maxChars &&
              textLimitAttempted
            ) {
              setTextLimitAttempted(false);
            }
            field.onChange(nextValue);
            syncFieldChange(nextValue as PathValue<T, K>);
            if (error) void trigger(name);
          };

          return (
            <>
              <FormItemFieldRenderer
                type={type}
                name={String(name)}
                label={label}
                labelId={labelId}
                testId={testId}
                dir={dir}
                disabled={disabled}
                required={required}
                options={options}
                field={field}
                error={displayError}
                validationLabel={labelForValidation}
                defaultValue={defaultValue}
                maxDigits={maxDigits}
                decimals={decimals}
                canBeginWithZero={canBeginWithZero}
                disableNativeNumberPattern={disableNativeNumberPattern}
                maxChars={maxChars}
                minRows={minRows}
                slotProps={slotProps}
                horizontalRadioButtons={horizontalRadioButtons}
                timePicker={timePicker}
                dateConstraint={dateConstraint}
                minDate={minDate}
                maxDate={maxDate}
                datePickerSlotProps={datePickerSlotProps}
                combinedInput={combinedInput}
                forceHighZIndexPopper={forceHighZIndexPopper}
                maxFiles={maxFiles}
                fileVariant={fileVariant}
                uploadConditions={uploadConditions}
                allowedFileExtensions={allowedFileExtensions}
                files={files}
                setFiles={setFiles}
                uploadDescription={uploadDescription}
                allowReplaceExisting={allowReplaceExisting}
                uploadStoreKey={uploadStoreKey}
                fileValidation={fileValidation}
                notSuccess={notSuccess}
                autocompleteValue={autocompleteValue}
                inputLabel={inputLabel}
                onFocus={onFocus}
                formatOnChange={formatOnChange}
                onReject={onReject}
                onUpload={(key, uploadedFiles) => onUpload?.(key as K, uploadedFiles)}
                onRemoveFile={(fieldName, index) => onRemoveFile?.(fieldName as K, index)}
                clearError={() => clearErrors(name)}
                setError={(message, errorType) =>
                  setError(name as any, { type: errorType ?? "manual", message })
                }
                triggerField={() => void trigger(name)}
                onFieldChangeImmediate={(key, value) =>
                  onFieldChange?.(key as K, value as PathValue<T, K>)
                }
                onMaxCharAttempt={() => setTextLimitAttempted(true)}
                onCommitValue={commitValue}
                onBlurTrim={handleBlurTrim}
                validateDrop={validateDrop}
              />
              {children}
              <Box
                sx={{
                  display: "flex",
                  ...(shouldRenderRadioValidationBelowButtons
                    ? {
                        flexBasis: "100%",
                        width: "100%",
                        justifyContent: "flex-end",
                      }
                    : {}),
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    ...(shouldRenderRadioValidationBelowButtons
                      ? {
                          width: inlineRadioGroupBasis,
                        }
                      : {}),
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      width: "100%",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    {showErrorMsg && (
                      <FormHelperText
                        sx={{
                          ml: 0,
                          flex: 1,
                          fontWeight: 500,
                          fontSize: 12,
                          marginY: "4px",
                          minHeight: "20px",
                          visibility: displayError || warningMsg ? "visible" : "hidden",
                          color: warningMsg ? "warning.main" : "error.main",
                        }}
                      >
                        {warningMsg ? warning : displayError}
                      </FormHelperText>
                    )}
                    {showCharCounter && maxChars ? (
                      <FormHelperText
                        sx={{
                          ml: "auto",
                          flexShrink: 0,
                          fontSize: 12,
                          textAlign: "right",
                          color: "text.secondary",
                        }}
                      >
                        {valueLength}/{maxChars}
                      </FormHelperText>
                    ) : null}
                  </Box>
                </Box>
              </Box>
            </>
          );
        }}
      />
    </FormControl>
  );
};

export default FormItem;
